import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TimelineGanttChart = ({ 
  tasks, 
  selectedTask, 
  onTaskSelect,
  timelineRange,
  onDependencyCreate 
}) => {
  const [zoom, setZoom] = useState(1);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const scrollRef = useRef(null);

  const daysInRange = Math.ceil(
    (new Date(timelineRange.end) - new Date(timelineRange.start)) / (1000 * 60 * 60 * 24)
  );

  const dayWidth = 60 * zoom;
  const rowHeight = 48;

  const getTaskPosition = (task) => {
    const taskStart = new Date(task.startDate);
    const rangeStart = new Date(timelineRange.start);
    const daysFromStart = Math.ceil((taskStart - rangeStart) / (1000 * 60 * 60 * 24));
    return daysFromStart * dayWidth;
  };

  const getTaskWidth = (task) => {
    return (task?.duration / 8) * dayWidth;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'var(--color-success)';
      case 'in-progress':
        return 'var(--color-primary)';
      case 'delayed':
        return 'var(--color-error)';
      case 'pending':
        return 'var(--color-warning)';
      default:
        return 'var(--color-muted-foreground)';
    }
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.2, 2));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.2, 0.5));
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({ x: e?.clientX, scrollLeft: scrollRef?.current?.scrollLeft });
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !dragStart) return;
    const dx = e?.clientX - dragStart?.x;
    scrollRef.current.scrollLeft = dragStart?.scrollLeft - dx;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setDragStart(null);
  };

  const renderTimelineHeader = () => {
    const headers = [];
    for (let i = 0; i < daysInRange; i++) {
      const date = new Date(timelineRange.start);
      date?.setDate(date?.getDate() + i);
      headers?.push(
        <div
          key={i}
          className="flex-shrink-0 border-r border-border"
          style={{ width: `${dayWidth}px` }}
        >
          <div className="px-2 py-2 text-center">
            <div className="text-xs font-medium text-foreground">
              {date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </div>
            <div className="caption text-muted-foreground">
              {date?.toLocaleDateString('en-US', { weekday: 'short' })}
            </div>
          </div>
        </div>
      );
    }
    return headers;
  };

  const renderDependencyLines = () => {
    const lines = [];
    tasks?.forEach((task, taskIndex) => {
      if (task?.dependencies && task?.dependencies?.length > 0) {
        task?.dependencies?.forEach(depId => {
          const depTask = tasks?.find(t => t?.id === depId);
          if (depTask) {
            const depIndex = tasks?.indexOf(depTask);
            const startX = getTaskPosition(depTask) + getTaskWidth(depTask);
            const startY = depIndex * rowHeight + rowHeight / 2;
            const endX = getTaskPosition(task);
            const endY = taskIndex * rowHeight + rowHeight / 2;

            lines?.push(
              <g key={`${task?.id}-${depId}`}>
                <path
                  d={`M ${startX} ${startY} L ${startX + 20} ${startY} L ${startX + 20} ${endY} L ${endX} ${endY}`}
                  stroke="var(--color-primary)"
                  strokeWidth="2"
                  fill="none"
                  markerEnd="url(#arrowhead)"
                />
              </g>
            );
          }
        });
      }
    });
    return lines;
  };

  const flattenTasks = (taskList) => {
    let result = [];
    taskList?.forEach(task => {
      result?.push(task);
      if (task?.subTasks && task?.subTasks?.length > 0) {
        result = result?.concat(flattenTasks(task?.subTasks));
      }
    });
    return result;
  };

  const allTasks = flattenTasks(tasks);

  return (
    <div className="h-full flex flex-col bg-card">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-semibold text-foreground">Timeline View</h2>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              iconName="ZoomOut"
              onClick={handleZoomOut}
              disabled={zoom <= 0.5}
            />
            <span className="caption text-muted-foreground min-w-[60px] text-center">
              {Math.round(zoom * 100)}%
            </span>
            <Button
              variant="outline"
              size="sm"
              iconName="ZoomIn"
              onClick={handleZoomIn}
              disabled={zoom >= 2}
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" iconName="Calendar">
            Today
          </Button>
          <Button variant="outline" size="sm" iconName="Download">
            Export
          </Button>
        </div>
      </div>
      <div className="flex-1 overflow-hidden">
        <div className="h-full flex">
          <div className="w-64 flex-shrink-0 border-r border-border overflow-y-auto">
            <div className="sticky top-0 bg-muted border-b border-border px-4 py-3 z-10">
              <span className="text-sm font-medium text-foreground">Task Name</span>
            </div>
            {allTasks?.map((task, index) => (
              <div
                key={task?.id}
                className={`px-4 py-3 border-b border-border cursor-pointer transition-colors duration-250 ${
                  selectedTask?.id === task?.id ? 'bg-primary/10' : 'hover:bg-muted'
                }`}
                style={{ height: `${rowHeight}px` }}
                onClick={() => onTaskSelect(task)}
              >
                <p className="text-sm font-medium text-foreground truncate">{task?.name}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="caption text-muted-foreground">{task?.duration}h</span>
                  <span className="caption text-muted-foreground">•</span>
                  <span className="caption text-muted-foreground">{task?.workers} workers</span>
                </div>
              </div>
            ))}
          </div>

          <div
            ref={scrollRef}
            className="flex-1 overflow-auto"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
          >
            <div className="sticky top-0 bg-muted border-b border-border flex z-10">
              {renderTimelineHeader()}
            </div>

            <div className="relative" style={{ height: `${allTasks?.length * rowHeight}px` }}>
              <svg
                className="absolute inset-0 pointer-events-none"
                style={{ width: `${daysInRange * dayWidth}px`, height: '100%' }}
              >
                <defs>
                  <marker
                    id="arrowhead"
                    markerWidth="10"
                    markerHeight="10"
                    refX="9"
                    refY="3"
                    orient="auto"
                  >
                    <polygon points="0 0, 10 3, 0 6" fill="var(--color-primary)" />
                  </marker>
                </defs>
                {renderDependencyLines()}
              </svg>

              {allTasks?.map((task, index) => (
                <div
                  key={task?.id}
                  className="absolute"
                  style={{
                    left: `${getTaskPosition(task)}px`,
                    top: `${index * rowHeight + 8}px`,
                    width: `${getTaskWidth(task)}px`,
                    height: `${rowHeight - 16}px`
                  }}
                >
                  <div
                    className={`h-full rounded-lg border-2 transition-all duration-250 ${
                      selectedTask?.id === task?.id ? 'ring-2 ring-primary' : ''
                    }`}
                    style={{
                      backgroundColor: `${getStatusColor(task?.status)}20`,
                      borderColor: getStatusColor(task?.status)
                    }}
                    onClick={() => onTaskSelect(task)}
                  >
                    <div className="px-2 py-1 h-full flex items-center justify-between">
                      <span className="text-xs font-medium text-foreground truncate">
                        {task?.progress}%
                      </span>
                      {task?.isMilestone && (
                        <Icon name="Flag" size={14} color={getStatusColor(task?.status)} />
                      )}
                    </div>
                    <div
                      className="absolute bottom-0 left-0 h-1 rounded-b-lg transition-all duration-250"
                      style={{
                        width: `${task?.progress}%`,
                        backgroundColor: getStatusColor(task?.status)
                      }}
                    />
                  </div>
                </div>
              ))}

              {Array.from({ length: daysInRange })?.map((_, i) => (
                <div
                  key={i}
                  className="absolute top-0 bottom-0 border-r border-border/50"
                  style={{ left: `${i * dayWidth}px` }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimelineGanttChart;